import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  // Color Palette - Professional, high-contrast enterprise scheme
  palette: {
    primary: {
      main: '#1976d2',      // MUI blue - trustworthy, professional
      light: '#42a5f5',     // For hover states
      dark: '#1565c0',      // For active states
      contrastText: '#fff',
    },
    secondary: {
      main: '#455a64',      // Blue-grey - subtle, professional
      light: '#718792',
      dark: '#1c313a',
      contrastText: '#fff',
    },
    error: {
      main: '#d32f2f',      // Critical alerts, overdue payments
      light: '#ef5350',
      dark: '#c62828',
    },
    warning: {
      main: '#f57c00',      // Payment warnings, compliance flags
      light: '#ff9800',
      dark: '#e65100',
    },
    success: {
      main: '#388e3c',      // Payments received, case closed
      light: '#4caf50',
      dark: '#2e7d32',
    },
    info: {
      main: '#0288d1',      // Informational notices
      light: '#03a9f4',
      dark: '#01579b',
    },
    background: {
      default: '#f5f7fa',   // Subtle off-white, reduces eye strain
      paper: '#ffffff',     // Pure white for data containers
    },
    divider: '#e0e0e0',     // Clear but not harsh separation
    text: {
      primary: '#212121',   // High contrast for readability
      secondary: '#616161', // Secondary info, labels
      disabled: '#9e9e9e',
    },
    // Custom status colors for collection workflows
    status: {
      active: '#1976d2',
      pending: '#f57c00',
      overdue: '#d32f2f',
      legal: '#7b1fa2',     // Purple for legal proceedings
      settled: '#388e3c',
      written_off: '#616161',
    },
  },

  // Typography - Optimized for dense data displays
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    
    // Rationale: System fonts load instantly, look professional, optimized for screens
    
    fontSize: 13,           // Slightly smaller base for density (default is 14)
    
    h1: {
      fontSize: '1.75rem',  // 22.75px - Page headers
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h2: {
      fontSize: '1.5rem',   // 19.5px - Section headers
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.25rem',  // 16.25px - Subsection headers
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.125rem', // 14.625px - Card headers
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1rem',     // 13px - Minor headers
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '0.875rem', // 11.375px - Labels as headers
      fontWeight: 600,
      lineHeight: 1.5,
      textTransform: 'none',
      letterSpacing: '0.05em',
    },
    body1: {
      textTransform: 'none',
      fontSize: '0.875rem', // 11.375px - Primary data text
      lineHeight: 1.5,
    },
    body2: {
      textTransform: 'none',
      fontSize: '0.8125rem', // 10.56px - Secondary data text
      lineHeight: 1.43,
    },
    button: {
      fontSize: '0.8125rem', // 10.56px - Button text
      fontWeight: 500,
      textTransform: 'none', // Sentence case is more professional than ALL CAPS
      letterSpacing: '0.02em',
    },
    caption: {
      fontSize: '0.75rem',  // 9.75px - Metadata, timestamps
      lineHeight: 1.4,
      color: '#616161',
    },
    overline: {
      fontSize: '0.6875rem', // 8.9375px - Table column headers, chips
      fontWeight: 600,
      lineHeight: 2,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    },
  },

  // Shape - Conservative, professional corners
  shape: {
    borderRadius: 4,        // Subtle rounding (default is 4, keep it)
  },

  // Spacing - Compact but not cramped
  spacing: 8,               // Keep default 8px base unit
  // Use multipliers: 0.5 (4px), 1 (8px), 1.5 (12px), 2 (16px), 3 (24px)

  // Shadows - Minimal, functional depth
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',           // 1 - Subtle lift
    '0px 1px 4px rgba(0, 0, 0, 0.08)',           // 2 - Cards
    '0px 2px 6px rgba(0, 0, 0, 0.1)',            // 3 - Dropdowns
    '0px 4px 8px rgba(0, 0, 0, 0.12)',           // 4 - Modals
    '0px 6px 12px rgba(0, 0, 0, 0.15)',          // 5 - Popovers
    // ... rest maintain light shadows
    '0px 1px 2px rgba(0, 0, 0, 0.05)',
    '0px 1px 4px rgba(0, 0, 0, 0.08)',
    '0px 2px 6px rgba(0, 0, 0, 0.1)',
    '0px 4px 8px rgba(0, 0, 0, 0.12)',
    '0px 6px 12px rgba(0, 0, 0, 0.15)',
    '0px 8px 16px rgba(0, 0, 0, 0.15)',
    '0px 10px 20px rgba(0, 0, 0, 0.15)',
    '0px 12px 24px rgba(0, 0, 0, 0.15)',
    '0px 14px 28px rgba(0, 0, 0, 0.15)',
    '0px 16px 32px rgba(0, 0, 0, 0.15)',
    '0px 18px 36px rgba(0, 0, 0, 0.15)',
    '0px 20px 40px rgba(0, 0, 0, 0.15)',
    '0px 22px 44px rgba(0, 0, 0, 0.15)',
    '0px 24px 48px rgba(0, 0, 0, 0.15)',
    '0px 26px 52px rgba(0, 0, 0, 0.15)',
    '0px 28px 56px rgba(0, 0, 0, 0.15)',
    '0px 30px 60px rgba(0, 0, 0, 0.15)',
    '0px 32px 64px rgba(0, 0, 0, 0.15)',
    '0px 34px 68px rgba(0, 0, 0, 0.15)',
  ],

  // Component-specific overrides
  components: {
    
    // === BUTTONS ===
    MuiButton: {
      defaultProps: {
        disableElevation: true, // Flat appearance, enterprise standard
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          padding: '6px 16px',
          minHeight: 32,        // Compact height
          fontWeight: 500,
          transition: 'all 0.15s ease-in-out',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          },
          '&:active': {
            boxShadow: 'none',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
          },
        },
        sizeSmall: {
          padding: '4px 12px',
          minHeight: 28,
          fontSize: '0.75rem',
        },
        sizeLarge: {
          padding: '8px 20px',
          minHeight: 40,
          fontSize: '0.9375rem',
        },
      },
    },

    // === CARDS & PAPER ===
    MuiCard: {
      defaultProps: {
        elevation: 1,
      },
      styleOverrides: {
        root: {
          borderRadius: 6,
          border: '1px solid #e0e0e0',
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
        },
        title: {
          fontSize: '0.9375rem',
          fontWeight: 600,
          color: '#212121',
        },
        subheader: {
          fontSize: '0.75rem',
          marginTop: '2px',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '16px',
          '&:last-child': {
            paddingBottom: '16px',
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 1,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove MUI gradient
        },
        rounded: {
          borderRadius: 6,
        },
      },
    },

    // === TABLES ===
    MuiTable: {
      defaultProps: {
        size: 'small',        // Dense by default
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          borderRadius: 6,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#f5f7fa',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e0e0e0',
          fontSize: '0.8125rem',
          padding: '10px 12px',
        },
        head: {
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#455a64',
          padding: '12px 12px',
          backgroundColor: '#f5f7fa',
          borderBottom: '2px solid #e0e0e0',
        },
        sizeSmall: {
          padding: '6px 10px',
          '&.MuiTableCell-head': {
            padding: '8px 10px',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#f5f7fa',
          },
          '&.Mui-selected': {
            backgroundColor: '#e3f2fd',
            '&:hover': {
              backgroundColor: '#bbdefb',
            },
          },
        },
      },
    },

    // === TABS ===
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderBottom: '2px solid #e0e0e0',
        },
        indicator: {
          height: 3,
          backgroundColor: '#1976d2',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          minWidth: 80,
          padding: '8px 16px',
          fontSize: '0.8125rem',
          fontWeight: 500,
          textTransform: 'none',
          color: '#616161',
          '&.Mui-selected': {
            color: '#1976d2',
            fontWeight: 600,
          },
          '&:hover': {
            color: '#1976d2',
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
          },
        },
      },
    },

    // === FORM FIELDS ===
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1976d2',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '2px',
          },
        },
        input: {
          padding: '8px 12px',
          fontSize: '0.875rem',
        },
        notchedOutline: {
          borderColor: '#e0e0e0',
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,         // Always show labels above fields
      },
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          fontWeight: 500,
          color: '#455a64',
          '&.Mui-focused': {
            color: '#1976d2',
          },
        },
        outlined: {
          transform: 'translate(14px, -9px) scale(0.9)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.9)',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          fontWeight: 500,
          color: '#455a64',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.6875rem',
          marginTop: '4px',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '8px 12px',
          minHeight: 'auto',
        },
      },
    },

    // === CHIPS (Status indicators) ===
    MuiChip: {
      styleOverrides: {
        root: {
          height: 24,
          fontSize: '0.6875rem',
          fontWeight: 600,
          borderRadius: 4,
        },
        label: {
          padding: '0 8px',
        },
        sizeSmall: {
          height: 20,
          fontSize: '0.625rem',
        },
      },
    },

    // === ALERTS ===
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          padding: '8px 12px',
          borderRadius: 6,
          border: '1px solid',
        },
        standardError: {
          backgroundColor: '#ffebee',
          borderColor: '#ef5350',
          color: '#c62828',
        },
        standardWarning: {
          backgroundColor: '#fff3e0',
          borderColor: '#ff9800',
          color: '#e65100',
        },
        standardInfo: {
          backgroundColor: '#e3f2fd',
          borderColor: '#42a5f5',
          color: '#01579b',
        },
        standardSuccess: {
          backgroundColor: '#e8f5e9',
          borderColor: '#4caf50',
          color: '#2e7d32',
        },
        message: {
          padding: '0',
        },
      },
    },

    // === DIALOGS / MODALS ===
    MuiDialog: {
      styleOverrides: {
        paper: {
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
          borderRadius: 8,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.125rem',
          fontWeight: 600,
          padding: '16px 20px',
          borderBottom: '1px solid #e0e0e0',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '20px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 20px',
          borderTop: '1px solid #e0e0e0',
        },
      },
    },

    // === TOOLTIPS ===
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#212121',
          fontSize: '0.75rem',
          padding: '6px 10px',
          borderRadius: 4,
        },
        arrow: {
          color: '#212121',
        },
      },
    },

    // === DIVIDERS ===
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#e0e0e0',
        },
      },
    },
  },
});

export default theme;